import { ImageIcon, Loader2, Star, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { type FeedbackEntry, useAdminFeedbackScreenshot, useDeleteFeedback } from "@/lib/api";
import {
  FEEDBACK_CATEGORY_VALUES,
  type FeedbackCategory,
  feedbackCategoryLabel,
  feedbackCategoryPillClass,
} from "@/lib/feedback";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

/** One feedback card in the admin list: meta row, message, lazy screenshot. */
export function FeedbackEntryCard({ entry }: { entry: FeedbackEntry }) {
  const created = new Date(entry.created_at);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // The list payload only carries `has_screenshot` — the PNG itself is
  // fetched per visible row (at most one page's worth at a time).
  const screenshotQuery = useAdminFeedbackScreenshot(entry.id, entry.has_screenshot ?? false);
  const screenshot = screenshotQuery.data?.screenshot_data_url ?? null;

  const deleteFeedback = useDeleteFeedback();

  const handleDelete = () => {
    if (!window.confirm("Delete this feedback entry? This can't be undone.")) {
      return;
    }
    deleteFeedback.mutate(
      { feedbackId: entry.id },
      {
        onError: (err) => {
          logger.warn("[admin-feedback] delete failed", err);
        },
      },
    );
  };

  return (
    <li className="border-border bg-card rounded-md border p-4">
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-foreground text-sm font-medium">{entry.user_email}</span>
          <CategoryPill category={entry.category ?? "general"} />
          {entry.has_screenshot && (
            <span
              className="bg-warm-gray-02 text-muted ring-warm-gray-05/30 inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium tracking-[0.04em] uppercase ring-1"
              title="Screenshot attached"
            >
              <ImageIcon className="h-3 w-3" />
              Screenshot
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {entry.rating != null && (
            <div className="flex items-center gap-0.5" aria-label={`Rated ${entry.rating} of 5`}>
              {[1, 2, 3, 4, 5].map((value) => (
                <Star
                  key={value}
                  className={cn(
                    "h-3.5 w-3.5",
                    value <= (entry.rating ?? 0)
                      ? "fill-jj-yellow-01 text-jj-yellow-01"
                      : "text-warm-gray-05",
                  )}
                />
              ))}
            </div>
          )}
          <time
            className="text-muted text-xs"
            dateTime={entry.created_at}
            title={created.toLocaleString()}
          >
            {created.toLocaleString()}
          </time>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleDelete}
            disabled={deleteFeedback.isPending}
            aria-label="Delete feedback"
            title="Delete feedback"
          >
            {deleteFeedback.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="text-destructive h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      <p className="text-foreground text-sm whitespace-pre-wrap">{entry.message}</p>
      {entry.source_path && (
        <p className="text-muted mt-2 text-xs">
          from <span className="font-mono">{entry.source_path}</span>
        </p>
      )}
      {entry.has_screenshot && (
        <div className="mt-3">
          {screenshotQuery.isError ? (
            <p className="text-destructive text-xs">Couldn&apos;t load screenshot.</p>
          ) : screenshot ? (
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="border-border hover:border-primary block w-full overflow-hidden rounded-md border transition-colors"
              title="Open screenshot"
            >
              {/* Full-row-width thumbnail at natural aspect ratio. No
                  max-height crop — readability beats layout neatness here,
                  and a click opens the lightbox for inspection. */}
              <img src={screenshot} alt="Attached screenshot" className="block h-auto w-full" />
            </button>
          ) : (
            <div className="border-border text-muted flex items-center gap-2 rounded-md border border-dashed px-3 py-4 text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading screenshot…
            </div>
          )}
        </div>
      )}
      {deleteFeedback.isError && (
        <p className="text-destructive mt-2 text-xs">Couldn&apos;t delete entry.</p>
      )}
      {screenshot && lightboxOpen && (
        <ScreenshotLightbox src={screenshot} onClose={() => setLightboxOpen(false)} />
      )}
    </li>
  );
}

function ScreenshotLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  // Locks body scroll so a wide screenshot doesn't fight with the underlying
  // page, and listens for ESC so a keyboard user can dismiss without hunting
  // for the close button. A single image + a backdrop click handler keeps
  // this lighter than a full Radix Dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Screenshot preview"
      onClick={onClose}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
        className="bg-card text-foreground hover:bg-warm-gray-02 absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-md border shadow-md"
      >
        <X className="h-4 w-4" />
      </button>
      {/* Stop propagation so clicking the image itself doesn't dismiss —
          only the backdrop / close button do. */}
      <img
        src={src}
        alt="Attached screenshot"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[95vh] max-w-[95vw] cursor-default rounded-md object-contain shadow-2xl"
      />
    </div>
  );
}

function CategoryPill({ category }: { category: string }) {
  const known = FEEDBACK_CATEGORY_VALUES.includes(category as FeedbackCategory);
  return (
    <span
      className={cn(
        "rounded-sm px-1.5 py-0.5 text-[10px] font-medium tracking-[0.04em] uppercase",
        known ? feedbackCategoryPillClass(category) : feedbackCategoryPillClass("general"),
      )}
    >
      {feedbackCategoryLabel(category)}
    </span>
  );
}
